// @flow
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { UnauthenticatedRedirect, userAddressConnector } from '../../user';
// Components
import { Helmet } from 'react-helmet';
import { Redirect } from 'react-router-dom';
import { ColorRow, PageLoader } from '../../common';
import CheckoutPageComponent from '../components/CheckoutPage';
import Lottie from 'lottie-react';
// Styles
import './CheckoutPage.css';
//Helpers
import { useScreenTimeLogger } from '../../common';
// Assets
import loadingCheckout from '../images/loading_checkout.json';
// Action creators
import {
  dismissErrors,
  fetchDeliveryOptionsCount,
  findCheckoutOrder,
  findItemTotalPromotions,
  initiateCheckout,
  submitAddress,
  submitConfirmation,
  submitDelivery,
  submitPayment,
  submitPromotion,
  transitionStepToState,
  transitionToState,
  removeCouponCode,
} from '../actions';
// Selectors
import { bigClubSettingsSelector } from '../../preferences/selectors';
import {
  addressErrorsSelector,
  adjustmentTotalSelector,
  couponCodesAppliedSelector,
  allPromotionsSelector,
  appliedStoreCreditsSelector,
  availableStoreCreditsSelector,
  creditCardsSelector,
  currentOrderBigClubInfoSelector,
  currentOrderDeliverySelector,
  currentOrderPaymentSelector,
  currentOrderShippingAddressSelector,
  deliveryErrorsSelector,
  gettingOrderLoadingSelector,
  gettingPromotionsLoadingSelector,
  initiatingCheckoutLoadingSelector,
  itemCountSelector,
  itemSubtotalSelector,
  itemsSelector,
  orderNumberSelector,
  orderStateSelector,
  orderTakeoutAddressSelector,
  paymentErrorsSelector,
  paymentMethodsSelector,
  promotionErrorsSelector,
  shippingRatesSelector,
  shippingSubtotalSelector,
  totalSelector,
  transitioningLoadingSelector,
  transitioningStepToSateLoadingSelector,
  deliveryOptionsCountSelector,
  applyingPromotionLoadingSelector,
} from '../selectors';
import { userTypeSelector } from '../../user/selectors';
import { blendsByOrderNumberSelector } from '../../blend/selectors';
//Connectors
import blendsListConnector from '../../blend/connectors/blendsListConnector';
// Types
import type { Connector } from 'react-redux';
import { BigClubSettings } from '../../preferences/types';
import type { UserAddress } from '../../user';
import type { Props as ComponentProps } from '../components/CheckoutPage';
import type {
  CreditCard,
  PaymentMethod,
  PaymentSource,
  ShippingRate,
} from '../types';
// Hooks
import { useUtmManager } from '../../common/customHooks/useUtmManager';

type Props = ComponentProps & {
  orderNumber: string,
  initialLoading: boolean,
  initiateCheckout: () => void,
  transitionStepToPayment: () => void,
  //Connector
  getBlends: orderNumber => void,
  blendsByOrderNumber: orderNumber => void,
  findItemTotalPromotions: () => void,
  bigclubSettings: BigClubSettings,
  userType: 'new' | 'existent',
};

type OwnProps = {
  location: Location,
};

const TIME_TO_SHOW_LOADING = 2500;

const CheckoutPage = (props: Props & OwnProps) => {
  useScreenTimeLogger(props.location.pathname);
  const [minLoadingTimeElapsed, setMinLoadingTimeElapsed] = useState(false);
  const { getCurrentUtms, getVisitorLogId } = useUtmManager({
    orderNumber: props.orderNumber,
    userId: props.userId,
    enabled: false,
  });

  const attributionSnapshot = useRef({
    utms: null,
    visitorLogId: null,
    userType: props.userType,
  });

  const currentUtms = getCurrentUtms();
  const currentVisitorLogId = getVisitorLogId();

  if (currentUtms) attributionSnapshot.current.utms = currentUtms;
  if (currentVisitorLogId) {
    attributionSnapshot.current.visitorLogId = currentVisitorLogId;
  }
  if (props.orderState !== 'complete') {
    attributionSnapshot.current.userType = props.userType;
  }

  const confirmationState = {
    hasSource: !!attributionSnapshot.current.utms?.utm_source,
    isNewUser: attributionSnapshot.current.userType === 'new',
    visitorLogId: attributionSnapshot.current.visitorLogId,
    utmPayload: attributionSnapshot.current.utms,
  };

  useEffect(() => {
    if (!props.orderNumber) {
      props.findCheckoutOrder();
      props.getBlends(props.orderNumber);
    } else {
      (!props.orderState || props.orderState === 'cart') &&
        props.initiateCheckout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.orderNumber]);

  useEffect(() => {
    props.findItemTotalPromotions();
    props.fetchDeliveryOptionsCount();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMinLoadingTimeElapsed(true);
    }, TIME_TO_SHOW_LOADING);

    return () => clearTimeout(timer);
  }, []);

  const shouldShowLoading = props.initialLoading || !minLoadingTimeElapsed;

  if (shouldShowLoading) {
    return (
      <div>
        <ColorRow color="red" text="CHECKOUT" />
        <div className="checkout-page-loading-container ">
          <h2 className="checkout-page-loading-title">
            Deixando tudo pronto para você!
          </h2>
          <Lottie
            animationData={loadingCheckout}
            className="checkout-page-loading"
            loop={false}
          />
        </div>
      </div>
    );
  }

  // TODO: Reimplement this protection in a way that does not redirect user to home page if he refreshes the page
  // if (!props.orderNumber) {
  //   return <Redirect to="/" />;
  // }

  if (
    props.orderPayment &&
    props.orderPayment.sourceType === 'PixPayment' &&
    props.orderState === 'complete'
  ) {
    return (
      <Redirect
        to={{
          pathname: '/pix-payment',
          search: `?id=${props.orderNumber}`,
          state: confirmationState,
        }}
      />
    );
  }
  if (props.orderState === 'complete') {
    return (
      <Redirect
        to={{
          pathname: '/confirmacao',
          search: `?id=${props.orderNumber}`,
          state: confirmationState,
        }}
      />
    );
  }

  return (
    <UnauthenticatedRedirect>
      <Helmet>
        <title>Checkout - Bigdelivery, Seu açougue virtual</title>
      </Helmet>
      <CheckoutPageComponent {...props} />
    </UnauthenticatedRedirect>
  );
};

const mapStateToProps = (state, ownProps: {}) => {
  return {
    orderNumber: orderNumberSelector(state),
    // We should trigger this loading if we are either getting an order or transitioning it to checkout
    initialLoading:
      initiatingCheckoutLoadingSelector(state) ||
      gettingOrderLoadingSelector(state) ||
      transitioningStepToSateLoadingSelector(state),
    items: itemsSelector(state),
    totalItemPrice: itemSubtotalSelector(state),
    shippingPrice: shippingSubtotalSelector(state),
    totalPrice: totalSelector(state), // Includes shipping cost
    adjustmentTotal: adjustmentTotalSelector(state),
    couponCodesApplied: couponCodesAppliedSelector(state),
    availableStoreCredits: availableStoreCreditsSelector(state),
    appliedStoreCredits: appliedStoreCreditsSelector(state),
    numberOfItems: itemCountSelector(state),
    shippingRates: shippingRatesSelector(state),
    orderState: orderStateSelector(state),
    takeoutAddress: orderTakeoutAddressSelector(state),
    paymentMethods: paymentMethodsSelector(state),
    creditCards: creditCardsSelector(state),
    orderShippingAddress: currentOrderShippingAddressSelector(state),
    orderDelivery: currentOrderDeliverySelector(state),
    bigClubInfo: currentOrderBigClubInfoSelector(state),
    orderPayment: currentOrderPaymentSelector(state),
    promotions: allPromotionsSelector(state),
    promotionsLoading: gettingPromotionsLoadingSelector(state),
    transitioningLoading: transitioningLoadingSelector(state),
    addressErrors: addressErrorsSelector(state),
    deliveryErrors: deliveryErrorsSelector(state),
    promotionErrors: promotionErrorsSelector(state),
    paymentErrors: paymentErrorsSelector(state),
    bigClubSettings: bigClubSettingsSelector(state),
    blendsByOrderNumber: (orderNumber: string) =>
      blendsByOrderNumberSelector(state, orderNumber),
    deliveryOptionsCount: deliveryOptionsCountSelector(state),
    applyingPromotionLoading: applyingPromotionLoadingSelector(state),
    userType: userTypeSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  findCheckoutOrder() {
    dispatch(findCheckoutOrder());
  },
  initiateCheckout() {
    dispatch(initiateCheckout());
  },
  transitionToState(state: string) {
    dispatch(transitionToState(state));
  },
  transitionStepToPayment() {
    dispatch(transitionStepToState('payment'));
  },
  submitAddress(address: UserAddress) {
    dispatch(submitAddress(address));
  },
  submitShippingRate(shippingRate: ShippingRate) {
    dispatch(submitDelivery(shippingRate));
  },
  submitPayment(
    paymentMethod: PaymentMethod,
    paymentSource: CreditCard | PaymentSource,
    existingCard: boolean,
    bigClubCreditAmount: number,
    issueResolution: string | null = null,
  ) {
    dispatch(
      submitPayment(
        paymentMethod,
        paymentSource,
        existingCard,
        bigClubCreditAmount,
        issueResolution,
      ),
    );
  },
  submitConfirmation() {
    dispatch(submitConfirmation());
  },
  submitPromotion(couponCode: string) {
    dispatch(submitPromotion(couponCode));
  },
  removeCouponCode(couponCode: string) {
    dispatch(removeCouponCode(couponCode));
  },
  dismissErrors() {
    dispatch(dismissErrors());
  },
  findItemTotalPromotions() {
    dispatch(findItemTotalPromotions());
  },
  fetchDeliveryOptionsCount() {
    dispatch(fetchDeliveryOptionsCount());
  },
});

const connector: Connector<OwnProps, Props> = connect(
  mapStateToProps,
  mapDispatchToProps,
);
export default withRouter(
  connector(userAddressConnector(blendsListConnector(CheckoutPage))),
);
